import React from "react";
import { Button } from "../ui/button";

/**
 * ConfirmationModal is a flexible modal for confirming destructive or important actions.
 *
 * Props:
 * - open: boolean (required)
 * - onConfirm: function (required)
 * - onCancel: function (required)
 * - title: string (optional, default: "Are you sure?")
 * - description: string (optional, default: "This action cannot be undone.")
 * - confirmText: string (optional, default: "Confirm")
 * - cancelText: string (optional, default: "Cancel")
 * - loading: boolean (optional, default: false)
 * - icon: ReactNode (optional, renders above the title)
 * - type: string (optional, e.g. "delete", "warning", "info")
 * - children: ReactNode (optional, renders between description and buttons)
 */
const ConfirmationModal = ({
  open,
  onConfirm,
  onCancel,
  title = "Are you sure?",
  description = "This action cannot be undone.",
  confirmText = "Confirm",
  cancelText = "Cancel",
  loading = false,
  icon = null,
  type = "default",
  children = null,
}) => {
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40">
      <div className="bg-white rounded-lg shadow-lg p-6 w-full max-w-md">
        {icon && <div className="flex justify-center mb-2">{icon}</div>}
        <h2 className="text-lg font-semibold mb-2 text-center">{title}</h2>
        <p className="mb-6 text-gray-700 text-center">{description}</p>
        {children && <div className="mb-6">{children}</div>}
        <div className="flex justify-end gap-2">
          <Button variant="outline" onClick={onCancel} disabled={loading}>
            {cancelText}
          </Button>
          <Button
            variant={type === "delete" ? "destructive" : "primary"}
            onClick={onConfirm}
            disabled={loading}
          >
            {loading ? "Processing..." : confirmText}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default ConfirmationModal;
